"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useStoreBuilderContext } from "@/ui/admin/store-builder/store-builder-context";
import {
	DndContext,
	type DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	SortableContext,
	arrayMove,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Columns3Icon, GripVerticalIcon, ListCollapseIcon, Trash2Icon } from "lucide-react";
import { nanoid } from "nanoid";
import { useState } from "react";
import type { YnsComponentForm } from "../types";
import type { Expandable } from "./expandable";

type Item = {
	id: string;
	title?: string | null;
	content?: unknown | null;
};

export const ExpandableForm: YnsComponentForm<typeof Expandable> = ({ data }) => {
	const context = useStoreBuilderContext<typeof Expandable>();

	const [items, setItems] = useState(data?.expandable?.map((tab) => ({ ...tab, id: nanoid() })) ?? []);

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;

		if (!over) return;

		if (active.id !== over.id) {
			const oldIndex = items.findIndex((item) => item.id === active.id);
			const newIndex = items.findIndex((item) => item.id === over.id);

			const udpatedItems = arrayMove(items, oldIndex, newIndex);
			setItems(udpatedItems);
			context.saveSelectedPageSectionField({ fieldPath: "expandable", value: udpatedItems });
		}
	};

	const handleItemUpdate = (id: string, newItem: Item) => {
		const udpatedItems = items.map((item) => (item.id === id ? newItem : item));
		setItems(udpatedItems);
		context.saveSelectedPageSectionField({ fieldPath: "expandable", value: udpatedItems });
	};

	const handleDeleteItem = (id: string) => {
		const udpatedItems = items.filter((item) => item.id !== id);
		setItems(udpatedItems);
		context.saveSelectedPageSectionField({ fieldPath: "expandable", value: udpatedItems });
	};

	const addNewItem = () => {
		const newItem: Item = {
			id: nanoid(),
			title: "",
			content: undefined,
		};

		setItems((items) => [...items, newItem]);
	};

	return (
		<div>
			<Tabs
				defaultValue={data?.layout ?? "accordion"}
				className="w-full"
				onValueChange={(value) => {
					context.saveSelectedPageSectionField({
						fieldPath: "layout",
						value,
					});
				}}
			>
				<TabsList className="grid w-full grid-cols-2">
					<TabsTrigger value="accordion">
						<ListCollapseIcon className="w-4 h-4 mr-1" />
						Accordion
					</TabsTrigger>
					<TabsTrigger value="tabs">
						<Columns3Icon className="w-4 h-4 mr-1" />
						Tabs
					</TabsTrigger>
				</TabsList>
			</Tabs>
			<div className="space-y-2">
				<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
					<SortableContext items={items.map((item) => item.id)} strategy={verticalListSortingStrategy}>
						<div className="space-y-1">
							{items.map((item) => (
								<SortableItem
									key={item.id}
									item={item}
									onDeleteItem={handleDeleteItem}
									onItemUpdate={handleItemUpdate}
								/>
							))}
						</div>
					</SortableContext>
				</DndContext>

				<Button type="button" className="w-full rounded-full" onClick={addNewItem}>
					Add Menu Item
				</Button>
			</div>
		</div>
	);
};

function SortableItem({
	item,
	onDeleteItem,
	onItemUpdate,
}: {
	item: Item;
	onItemUpdate: (id: string, title: Item) => void;
	onDeleteItem: (id: string) => void;
}) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: item.id,
	});

	const style = {
		transform: CSS.Translate.toString(transform),
		transition,
		zIndex: isDragging ? 1 : 0,
		opacity: isDragging ? 0.8 : 1,
		position: "relative" as const,
	};

	return (
		<div
			ref={setNodeRef}
			style={style}
			className="p-2 flex items-center gap-1 bg-white"
			data-row-id={item.id}
		>
			<div
				className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 rounded touch-none"
				{...attributes}
				{...listeners}
			>
				<GripVerticalIcon className="h-5 w-5 text-gray-400" />
			</div>
			<div className="flex-1 flex flex-row items-center gap-2">
				<Input
					value={item.title ?? ""}
					onChange={(e) => {
						const value = e.target.value;
						onItemUpdate(item.id, { ...item, title: value });
					}}
					className="h-8"
					placeholder="Key"
				/>
			</div>
			<Button size="sm" variant="ghost" onClick={() => onDeleteItem(item.id)}>
				<Trash2Icon className="h-4 w-4 text-destructive" />
				<span className="sr-only">Delete</span>
			</Button>
		</div>
	);
}
